import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import * as THREE from 'three';
import {
  FaArrowRight,
  FaChartLine,
  FaDatabase,
  FaEnvelope,
  FaLinkedin,
  FaMicrosoft,
  FaPython,
  FaServer,
  FaShieldAlt,
} from 'react-icons/fa';
import './index.css';

const links = {
  email: 'jav.sid94@protonmail.com',
  linkedin: 'https://linkedin.com/in/javeriya-sayeed',
};

const navLinks = [
  { href: '#about', label: 'About' },
  { href: '#tech', label: 'Stack' },
  { href: '#specialisms', label: 'Skills' },
  { href: '#projects', label: 'Work' },
  { href: '#experience', label: 'Exp' },
  { href: '#contact', label: 'Contact' },
];

const techGroups = [
  {
    title: 'Core focus',
    items: [
      'Microsoft Azure', 'Microsoft Fabric', 'Azure Data Factory', 'Azure Databricks',
      'Power BI', 'SQL Server', 'Python', 'PySpark', 'ETL Pipelines', 'Delta Lake',
    ],
  },
  {
    title: 'Also worked with',
    items: [
      'SSIS', 'Azure Blob Storage', 'Power Automate', 'Excel Automation', 'Pandas',
      'Data Modelling', 'RBAC', 'Data Warehousing', 'Jupyter', 'Stakeholder Reporting',
    ],
  },
];

const specialisms = [
  {
    id: 'fabric',
    label: 'Fabric Migration',
    description: 'Migrating SQL Server and Azure Data Factory assets into Microsoft Fabric Lakehouse architecture.',
    tags: ['Fabric', 'Lakehouse', 'Dataflows'],
    icon: <FaMicrosoft />,
  },
  {
    id: 'pipelines',
    label: 'Pipeline Engineering',
    description: 'Designing reliable ingestion, transformation, orchestration, and automated data movement.',
    tags: ['ADF', 'ETL', 'PySpark'],
    icon: <FaServer />,
  },
  {
    id: 'bi',
    label: 'BI Delivery',
    description: 'Building Power BI reporting models and dashboards that support operational decisions.',
    tags: ['Power BI', 'SQL', 'Dashboards'],
    icon: <FaChartLine />,
  },
  {
    id: 'governance',
    label: 'Governance',
    description: 'Implementing access control and data practices that make reporting secure and maintainable.',
    tags: ['RBAC', 'Security', 'Quality'],
    icon: <FaShieldAlt />,
  },
  {
    id: 'automation',
    label: 'Python Automation',
    description: 'Transforming Excel-heavy workflows into repeatable Python and Databricks processes.',
    tags: ['Python', 'Pandas', 'Databricks'],
    icon: <FaPython />,
  },
  {
    id: 'modelling',
    label: 'Data Modelling',
    description: 'Structuring source data into models that are easier to query, govern, and explain.',
    tags: ['SQL Server', 'Models', 'Warehousing'],
    icon: <FaDatabase />,
  },
];

const projects = [
  {
    name: 'Microsoft Fabric Migration',
    oneLiner: 'Migrated reporting and pipeline workloads from SQL Server and ADF into Microsoft Fabric.',
    tags: ['Fabric', 'ADF', 'SQL Server'],
    impact: 'Created a cleaner lakehouse platform with governed access and faster reporting paths.',
  },
  {
    name: 'Wildlife Strike Dashboard',
    oneLiner: 'Built Power BI reporting over 15 years of airport wildlife strike data.',
    tags: ['Power BI', 'SSIS', 'Analytics'],
    impact: 'Helped stakeholders identify high-risk trends and operational patterns.',
  },
  {
    name: 'Databricks ETL Automation',
    oneLiner: 'Automated Excel ingestion into Azure Databricks Delta Lake tables for energy data.',
    tags: ['Databricks', 'Python', 'Delta Lake'],
    impact: 'Reduced manual collection and transformed spreadsheet workflows into repeatable pipelines.',
  },
];

const experience = [
  {
    company: 'Foundation House',
    role: 'Senior Business Intelligence Developer (Synapse & Microsoft Fabric)',
    period: 'Dec 2025 - Present',
    location: 'Melbourne, VIC',
    bullets: [
      'Migrated a complex, metadata-driven Salesforce data pipeline from Azure Synapse to Microsoft Fabric, modernising the organization\'s core data infrastructure.',
      'Engineered extraction of 50 Salesforce objects via REST API, enabling scalable and reliable ingestion into the Fabric data lakehouse.',
      'Implemented the metadata-driven Fabric Accelerator framework, architecting Bronze, Silver, and Gold layers of the Medallion Architecture to standardise data transformation and lineage.',
      'Developed a custom Python transformation script to process Bronze-to-Silver layer data, implementing de-duplication logic and UTC-to-Australian timezone conversion to ensure data quality and regional accuracy.',
      'Partnered with stakeholders to verify data integrity across 3 Executive Leadership dashboards, restoring reporting accuracy critical to senior decision-making.',
      'Diagnosed and resolved underlying data model defects in Power BI that had been causing reporting discrepancies across the Executive dashboards.',
    ],
  },
  {
    company: 'Kinrise',
    role: 'Azure Database Administrator',
    period: 'Sep 2024 - Sep 2025',
    location: 'Melbourne, VIC · Hybrid',
    bullets: [
      'Leading migration of SQL Server and Azure Data Factory assets into Microsoft Fabric.',
      'Re-engineered ADF pipelines into Fabric Dataflows and Data Pipelines.',
      'Implemented RBAC security configurations and improved platform governance.',
      'Built Power BI reporting layers for operational and leadership visibility.',
    ],
  },
  {
    company: 'FDM Group',
    role: 'Business Intelligence Analyst',
    period: 'Aug 2023 - Sep 2024',
    location: 'Remote',
    bullets: [
      'Designed Power BI dashboards over 15 years of wildlife strike data.',
      'Migrated historical data using SSIS and SQL Server workflows.',
      'Consolidated multiple sources into stakeholder-ready analysis models.',
    ],
  },
  {
    company: 'Shell Energy Australia',
    role: 'Python Business Analyst',
    period: 'Jan 2024 - Jun 2024',
    location: 'On-site',
    bullets: [
      'Automated energy data transformation from Excel into Databricks Delta Lake tables.',
      'Processed files from 10+ operators and 40+ pipelines.',
      'Built Python transformations using Pandas, PySpark, openpyxl, and xlrd.',
    ],
  },
  {
    company: 'RMIT University',
    role: 'Teaching Assistant',
    period: 'Mar 2021 - Oct 2022',
    location: 'Melbourne, Australia',
    bullets: [
      'Supported students with research skills, academic writing, and lab guidance.',
      'Provided marking, feedback, and mentoring for a cohort of 25 students.',
    ],
  },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1], delay },
});

const heroContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.11, delayChildren: 0.3 } },
};

const heroItem = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

function CursorTrail() {
  const [pos, setPos] = useState({ x: -100, y: -100 });
  const [ring, setRing] = useState({ x: -100, y: -100 });

  useEffect(() => {
    let raf = 0;
    const handleMove = (event) => {
      const next = { x: event.clientX, y: event.clientY };
      setPos(next);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setRing(next));
    };

    window.addEventListener('pointermove', handleMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', handleMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      <div className="cursor-dot" style={{ left: pos.x, top: pos.y }} />
      <div className="cursor-ring" style={{ left: ring.x, top: ring.y }} />
    </>
  );
}

function ThreeBackground() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 8;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6));
    renderer.setSize(window.innerWidth, window.innerHeight);
    mount.appendChild(renderer.domElement);

    const particleCount = 140;
    const positions = new Float32Array(particleCount * 3);
    const radius = 5.2;

    for (let i = 0; i < particleCount; i += 1) {
      const angle = (i / particleCount) * Math.PI * 2;
      const layer = (i % 7) - 3;
      const jitter = 0.8 + Math.random() * 1.6;
      positions[i * 3] = Math.cos(angle) * (radius + Math.sin(i) * jitter);
      positions[i * 3 + 1] = Math.sin(angle * 1.4) * 2.2 + layer * 0.18;
      positions[i * 3 + 2] = Math.sin(angle) * 1.5 - 1.2;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0xf05aa4,
      size: 0.035,
      transparent: true,
      opacity: 0.38,
      sizeAttenuation: true,
    });

    const points = new THREE.Points(geometry, material);
    points.position.x = 1.6;
    points.position.y = -0.2;
    scene.add(points);

    const linePositions = [];
    for (let i = 0; i < particleCount - 1; i += 5) {
      linePositions.push(
        positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2],
        positions[(i + 1) * 3], positions[(i + 1) * 3 + 1], positions[(i + 1) * 3 + 2]
      );
    }

    const lineGeometry = new THREE.BufferGeometry();
    lineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0xf05aa4,
      transparent: true,
      opacity: 0.08,
    });
    const lines = new THREE.LineSegments(lineGeometry, lineMaterial);
    lines.position.copy(points.position);
    scene.add(lines);

    let frameId = 0;
    const render = () => {
      if (!reduceMotion) {
        points.rotation.y += 0.0009;
        points.rotation.x = Math.sin(Date.now() * 0.00018) * 0.08;
        lines.rotation.copy(points.rotation);
      }
      renderer.render(scene, camera);
      frameId = requestAnimationFrame(render);
    };

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    render();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(frameId);
      geometry.dispose();
      material.dispose();
      lineGeometry.dispose();
      lineMaterial.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={mountRef} className="three-background" aria-hidden="true" />;
}

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`site-header ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="header-inner">
        <a href="#home" className="monogram">JS</a>
        <nav className="desktop-menu" aria-label="Primary navigation">
          {navLinks.map((link) => (
            <a key={link.href} href={link.href}>{link.label}</a>
          ))}
          <a className="connect-link" href={`mailto:${links.email}`}>Connect</a>
        </nav>
        <button className="menu-toggle" onClick={() => setMobileOpen((open) => !open)} aria-label="Menu">
          <span className={mobileOpen ? 'open' : ''} />
          <span className={mobileOpen ? 'open' : ''} />
          <span className={mobileOpen ? 'open' : ''} />
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="mobile-menu"
          >
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} onClick={() => setMobileOpen(false)}>{link.label}</a>
            ))}
            <a href={`mailto:${links.email}`} onClick={() => setMobileOpen(false)}>Connect -></a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

function Hero() {
  return (
    <section id="home" className="hero-section">
      <div className="hero-orbit" aria-hidden="true">
        <img src="/Jav_Avatar.png" alt="Illustrated portrait of Javeriya S" className="hero-portrait" />
      </div>
      <div className="hero-gradient" />

      <motion.div variants={heroContainer} initial="hidden" animate="show" className="hero-content">
        <motion.div variants={heroItem} className="section-tag">Available for data roles · Melbourne, AU</motion.div>
        <motion.h1 variants={heroItem} className="hero-title">
          Javeriya<br />
          <span>Sayeed</span>
        </motion.h1>
        <motion.p variants={heroItem} className="hero-copy">
          Data Engineer working across Azure, Microsoft Fabric, Databricks, SQL Server, and Power BI.
        </motion.p>
        <motion.div variants={heroItem} className="hero-pitch">
          <p>I design reliable pipelines, governed lakehouse layers, and reporting systems teams can actually trust.</p>
          <p>Practical engineering, clean delivery, and business context in the same workflow.</p>
        </motion.div>
        <motion.div variants={heroItem} className="hero-actions">
          <a href={`mailto:${links.email}`}>Get in touch</a>
          <a href={links.linkedin} target="_blank" rel="noreferrer">LinkedIn</a>
        </motion.div>
      </motion.div>
      <motion.div className="scroll-cue" animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }} />
    </section>
  );
}

function About() {
  const facts = [
    { label: 'Role', value: 'Data Engineer' },
    { label: 'Based', value: 'Melbourne, Australia' },
    { label: 'Focus', value: 'Azure · Fabric · BI' },
    { label: 'Current', value: 'Foundation House' },
  ];

  return (
    <section id="about" className="portfolio-section">
      <span className="ghost-number">01</span>
      <div className="section-inner">
        <motion.div {...fadeUp(0)} className="section-tag">About Me</motion.div>
        <div className="about-grid">
          <div>
            <motion.h2 {...fadeUp(0.1)} className="section-title">
              Data engineer.<br />
              <span>Insight builder.</span>
            </motion.h2>
            <motion.div {...fadeUp(0.22)} className="fact-list">
              {facts.map((fact) => (
                <div key={fact.label} className="fact-row">
                  <span>{fact.label}</span>
                  <i />
                  <strong>{fact.value}</strong>
                </div>
              ))}
            </motion.div>
          </div>
          <div className="about-copy">
            {[
              'I work where data engineering meets analytics delivery: cleaning up fragmented workflows, rebuilding pipeline logic, and creating reporting structures that can be owned by the business.',
              'My recent work focuses on Microsoft Fabric migration, Azure Data Factory refactoring, Power BI reporting, and Databricks automation.',
              'Beyond data, I care about sustainability, fashion, community work, and supporting women in STEM as a STEM Sisters Ambassador 2025.',
            ].map((text, index) => (
              <motion.p key={text} {...fadeUp(0.15 + index * 0.1)}>{text}</motion.p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TechStack() {
  const core = [...techGroups[0].items, ...techGroups[0].items];
  const also = [...techGroups[1].items, ...techGroups[1].items];

  return (
    <section id="tech" className="portfolio-section tech-section">
      <span className="ghost-number">02</span>
      <div className="section-inner tight-bottom">
        <motion.div {...fadeUp(0)} className="section-tag">What I Work With</motion.div>
        <motion.h2 {...fadeUp(0.1)} className="section-title tech-title">
          Cloud data stack,<br />
          <span>end to insight.</span>
        </motion.h2>
        <div className="marquee-label"><span>Core focus</span><i /></div>
      </div>
      <div className="marquee-wrap">
        <div className="marquee-track">
          {core.map((item, index) => <span key={`${item}-${index}`}>{item}</span>)}
        </div>
      </div>
      <div className="marquee-label section-inner"><span>Also worked with</span><i /></div>
      <div className="marquee-wrap secondary">
        <div className="marquee-track reverse">
          {also.map((item, index) => <span key={`${item}-${index}`}>{item}</span>)}
        </div>
      </div>
    </section>
  );
}

function Specialisms() {
  return (
    <section id="specialisms" className="portfolio-section">
      <span className="ghost-number">03</span>
      <div className="section-inner">
        <motion.div {...fadeUp(0)} className="section-tag">Deep Skills</motion.div>
        <motion.h2 {...fadeUp(0.1)} className="section-title compact">
          Specialist <span>domains.</span>
        </motion.h2>
        <div className="specialism-grid">
          {specialisms.map((item, index) => (
            <motion.article key={item.id} {...fadeUp(index * 0.06)} className="specialism-card">
              <div className="specialism-icon">{item.icon}</div>
              <h3>{item.label}</h3>
              <p>{item.description}</p>
              <div>
                {item.tags.map((tag) => <span key={tag}>{tag}</span>)}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Projects() {
  return (
    <section id="projects" className="portfolio-section">
      <span className="ghost-number">04</span>
      <div className="section-inner">
        <motion.div {...fadeUp(0)} className="section-tag">Selected Work</motion.div>
        <motion.h2 {...fadeUp(0.1)} className="section-title compact">
          Data products <span>delivered.</span>
        </motion.h2>
        <div className="project-layout">
          {projects.map((project, index) => (
            <motion.article key={project.name} {...fadeUp(index * 0.08)} className="project-tile">
              <div className="project-index">{String(index + 1).padStart(2, '0')}</div>
              <h3>{project.name}</h3>
              <p>{project.oneLiner}</p>
              <p className="impact">{project.impact}</p>
              <div>{project.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Experience() {
  const [expanded, setExpanded] = useState(0);

  return (
    <section id="experience" className="portfolio-section">
      <span className="ghost-number">05</span>
      <div className="section-inner">
        <motion.div {...fadeUp(0)} className="section-tag">Where I've Worked</motion.div>
        <motion.h2 {...fadeUp(0.1)} className="section-title compact">Experience.</motion.h2>
        <div className="experience-list">
          {experience.map((item, index) => (
            <motion.article key={`${item.company}-${item.role}`} {...fadeUp(index * 0.05)} className="experience-item">
              <button onClick={() => setExpanded(expanded === index ? null : index)}>
                <span className="period">{item.period}</span>
                <span className="role-block">
                  <strong>{item.role}</strong>
                  <em>{item.company}</em>
                  <small>{item.location}</small>
                </span>
                <motion.span animate={{ rotate: expanded === index ? 45 : 0 }} className="plus">+</motion.span>
              </button>
              <AnimatePresence initial={false}>
                {expanded === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                    className="experience-detail"
                  >
                    <ul>
                      {item.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [copied, setCopied] = useState(false);

  const copyEmail = () => {
    navigator.clipboard.writeText(links.email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  return (
    <section id="contact" className="contact-section">
      <div className="contact-ambient" />
      <div className="section-inner contact-inner">
        <motion.div {...fadeUp(0)} className="section-tag">Get In Touch</motion.div>
        <motion.h2 {...fadeUp(0.1)} className="contact-title">
          Let's<br />
          <span>build.</span>
        </motion.h2>
        <motion.p {...fadeUp(0.2)} className="contact-copy">
          Open to data engineering, Azure/Fabric migration, BI delivery, and pipeline automation conversations.
        </motion.p>
        <motion.div {...fadeUp(0.3)} className="contact-actions">
          <a href={`mailto:${links.email}`}>Send an email <FaArrowRight /></a>
          <button onClick={copyEmail}>{copied ? 'Copied' : 'Copy email'}</button>
        </motion.div>
        <div className="contact-row">
          <a href={`mailto:${links.email}`}><FaEnvelope /><span>Email</span><strong>{links.email}</strong></a>
          <a href={links.linkedin} target="_blank" rel="noreferrer"><FaLinkedin /><span>LinkedIn</span><strong>Connect</strong></a>
        </div>
        <footer>
          <span>© Javeriya S · {new Date().getFullYear()}</span>
          <strong>JS</strong>
        </footer>
      </div>
    </section>
  );
}

function App() {
  return (
    <div className="site-shell">
      <ThreeBackground />
      <CursorTrail />
      <Header />
      <motion.main initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <Hero />
        <About />
        <TechStack />
        <Specialisms />
        <Projects />
        <Experience />
        <Contact />
      </motion.main>
    </div>
  );
}

export default App;
